import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTemplates } from "../../hooks/usePromptTemplates";
import { PageHeader } from "../../components/ui/PageHeader";
import type { TemplateCategory, TemplateSummary } from "../../api/promptTemplates";
import {
  filterTemplates,
  sortTemplates,
  type TemplateSort,
} from "./templates-filter";
import { LanguageBadge, type Language } from "../../components/LanguageBadge";

// ─── Category badge colors ──────────────────────────────────────────────────────

const CATEGORY_COLORS: Record<TemplateCategory, string> = {
  credential: "var(--accent)",
  pii: "var(--warning, #d97706)",
  access: "var(--info, #3b82f6)",
  financial: "var(--error)",
  custom: "var(--text-muted)",
};

const CATEGORY_LABELS: Record<TemplateCategory, string> = {
  credential: "Credential",
  pii: "PII",
  access: "Access",
  financial: "Financial",
  custom: "Custom",
};

function CategoryBadge({ category }: { category: TemplateCategory }) {
  const color = CATEGORY_COLORS[category] ?? "var(--text-muted)";
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 10,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        color,
        background: `color-mix(in srgb, ${color} 12%, transparent)`,
        border: `1px solid color-mix(in srgb, ${color} 25%, transparent)`,
      }}
    >
      {CATEGORY_LABELS[category] ?? category}
    </span>
  );
}

// ─── Toolbar styles ─────────────────────────────────────────────────────────────

const selectStyle: React.CSSProperties = {
  background: "var(--bg-card)",
  border: "1px solid var(--border)",
  borderRadius: 6,
  padding: "5px 10px",
  fontSize: 12,
  color: "var(--text-primary)",
  cursor: "pointer",
  outline: "none",
};

const inputStyle: React.CSSProperties = {
  background: "var(--bg-card)",
  border: "1px solid var(--border)",
  borderRadius: 6,
  padding: "5px 10px",
  fontSize: 12,
  color: "var(--text-primary)",
  outline: "none",
  minWidth: 200,
};

// ─── Component ───────────────────────────────────────────────────────────────────

export function TemplatesList() {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useTemplates();

  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<TemplateSort>("name");
  const [languageFilter, setLanguageFilter] = useState<"all" | Language>("all");

  if (isLoading) {
    return (
      <div style={{ padding: 24, color: "var(--text-muted)", fontFamily: "var(--font-sans)" }}>
        Loading templates...
      </div>
    );
  }

  if (isError) {
    return (
      <div style={{ padding: 24, color: "var(--error)", fontFamily: "var(--font-sans)" }}>
        Failed to load templates.
      </div>
    );
  }

  const allItems = data?.items ?? [];
  const items = sortTemplates(filterTemplates(allItems, query, languageFilter), sort);

  return (
    <div style={{ fontFamily: "var(--font-sans)", maxWidth: 960 }}>
      <PageHeader
        eyebrow="Operator console"
        title="Template library"
        subtitle="Reusable prompt templates for social engineering pretexts. Apply a template to a persona to auto-fill system prompts and roles."
        meta={
          <span>
            <span style={{ color: "var(--text-secondary)" }}>{data?.total ?? 0}</span> total
          </span>
        }
      />

      {/* Search / sort / language toolbar */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 16,
          alignItems: "center",
        }}
      >
        <input
          type="search"
          placeholder="Search templates…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={inputStyle}
        />
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as TemplateSort)}
          style={selectStyle}
          aria-label="Sort templates"
        >
          <option value="name">Name</option>
          <option value="category">Category</option>
          <option value="newest">Newest</option>
        </select>
        <select
          value={languageFilter}
          onChange={(e) => setLanguageFilter(e.target.value as "all" | Language)}
          style={selectStyle}
          aria-label="Filter by language"
        >
          <option value="all">All languages</option>
          <option value="en">English</option>
          <option value="es">Spanish</option>
          <option value="hi">Hindi</option>
        </select>
      </div>

      {/* Template grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 14,
        }}
      >
        {/* + Create template card */}
        <div
          onClick={() => navigate("/templates/new")}
          style={{
            background: "var(--bg-card)",
            border: "2px dashed var(--border)",
            borderRadius: 12,
            padding: "28px 20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            cursor: "pointer",
            minHeight: 160,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLDivElement).style.borderColor = "var(--accent)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLDivElement).style.borderColor = "var(--border)";
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: "var(--accent)",
              color: "white",
              display: "grid",
              placeItems: "center",
              fontWeight: 700,
              fontSize: 18,
            }}
          >
            +
          </div>
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)" }}>
            Create template
          </span>
          <span style={{ fontSize: 10.5, color: "var(--text-muted)", textAlign: "center" }}>
            Build a new SE pretext template
          </span>
        </div>

        {/* Template cards */}
        {items.map((item: TemplateSummary) => (
          <TemplateCard key={item.template_id} item={item} />
        ))}
      </div>

      {/* Empty state — no results from filter */}
      {allItems.length > 0 && items.length === 0 && (
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--glass-border)",
            borderRadius: 12,
            padding: "48px 24px",
            textAlign: "center",
            color: "var(--text-muted)",
            fontSize: 13,
            lineHeight: 1.7,
            marginTop: 14,
          }}
        >
          No templates match.
        </div>
      )}

      {/* Empty state — no templates at all */}
      {allItems.length === 0 && (
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--glass-border)",
            borderRadius: 12,
            padding: "48px 24px",
            textAlign: "center",
            color: "var(--text-muted)",
            fontSize: 13,
            lineHeight: 1.7,
            marginTop: 14,
          }}
        >
          No templates yet. Create one or run the seed script to get started.
        </div>
      )}
    </div>
  );
}

function TemplateCard({ item }: { item: TemplateSummary }) {
  const navigate = useNavigate();

  return (
    <div
      onClick={() => navigate(`/templates/${item.template_id}`)}
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: 12,
        padding: "18px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        cursor: "pointer",
        minHeight: 160,
        boxShadow: "var(--shadow-card)",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = "var(--bg-hover, var(--bg-card))";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = "var(--bg-card)";
      }}
    >
      {/* Badges row: category + language */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <CategoryBadge category={item.category} />
        <LanguageBadge language={item.language as Language} />
      </div>

      {/* Name */}
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "var(--text-primary)",
          lineHeight: 1.3,
        }}
      >
        {item.name}
      </div>

      {/* Description */}
      <div
        style={{
          fontSize: 11,
          color: "var(--text-muted)",
          lineHeight: 1.5,
          flex: 1,
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
        }}
      >
        {item.description}
      </div>

      {/* Footer: suggested role + variable count */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingTop: 8,
          borderTop: "1px solid var(--border-subtle)",
        }}
      >
        <span style={{ fontSize: 10, color: "var(--text-muted)" }}>
          {item.suggested_role}
        </span>
        <span
          style={{
            fontSize: 9.5,
            fontWeight: 600,
            color: "var(--text-secondary)",
            background: "var(--bg-tertiary)",
            padding: "2px 7px",
            borderRadius: 10,
          }}
        >
          {item.variable_count} var{item.variable_count !== 1 ? "s" : ""}
        </span>
      </div>
    </div>
  );
}
